import { Ng2StateDeclaration, Transition } from '@uirouter/angular';
import { ComplianceItemService } from '../services/compliance-item.service';
import { RequestItemService } from '../services/request-item.service';
import { UserRequestService } from '../services/user-request.service';
import { PrintRosterComponent } from './print-roster.component';
import { PrintItemComponent } from './reports/item/print-item.component';
import { RequestsResolver } from '../resolvers/requests/requests.resolver';
import { BaseUser, SearchableComponent, SessionService } from '@edl-development/edl-common';
import { firstValueFrom } from 'rxjs';
import { CplUserRequest } from '../model/cpl-user-request.model';
import { UserArchivedFilter } from '../enums/user-request/user-archived-filter.enum';

export const LARGET_LIMIT = 2000000000;

export function resolveComplianceItem(complianceItemService: ComplianceItemService, transition: Transition) {
    const params = transition.params();
    const cplId: string = params.cplId;

    return complianceItemService
        .getComplianceItem(cplId, "")
        .toPromise();
}

export function resolveRequestItem(requestItemService: RequestItemService, transition: Transition) {
    const params = transition.params();
    const requestId: string = params.requestId;

    return firstValueFrom(requestItemService.getRequestItem(requestId, false));
}

export function resolveUsers_Full(
    transition: Transition,
    userRequestService: UserRequestService
) {
    const params = transition.params();
    const requestId: string = params.requestId;

    return firstValueFrom(userRequestService.getUsersWhoHaveSubmittedRequestItem(requestId));
}

export function resolveUsers_Filtered(
    transition: Transition,
    userRequestService: UserRequestService,
    sessionService: SessionService,
    requestsResolver: RequestsResolver
) {
    return requestsResolver.resolveUserRequestsForItem(transition, userRequestService, sessionService, 1, LARGET_LIMIT)
        .then(result => {
            const userRequest = result?.Results ?? [];

            const uniqueUserIds: Set<string> = new Set();
            return userRequest.reduce((acc: { FirstName: string, LastName: string }[], curr: CplUserRequest) => {
                if (!uniqueUserIds.has(curr.UserID)) {
                    uniqueUserIds.add(curr.UserID);
                    acc.push({
                        FirstName: curr.FirstName,
                        LastName: curr.LastName
                    });
                }
                return acc;
            }, [] as { FirstName: string, LastName: string }[]);
    });
}

export function resolveItemCycles(transition: Transition, complianceItemService: ComplianceItemService, complianceItem) {
    const params = transition.params();
    let itemCycleID = null;

    const searchBy: string = params.searchBy === undefined ? "" : SearchableComponent.UnPackageSearchParameter(params.searchBy);
    const statusId: number = params.statusId;
    const a48statusId: number = params.act48Status;
    const itemCycleId: string = params.itemCycleID;
    const approverUserId: string = params.approverUserId;
    const groupIds: string[] =  Array.isArray(params.groupIds) ? params.groupIds : params.groupIds ? [params.groupIds] : [];
    const cplId = complianceItem.ItemID;

    if (!itemCycleId) {
        if (complianceItem.ItemCycles.length > 0) {
            itemCycleID = complianceItem.ItemCycles[0].CycleID;
        }
    } else {
        itemCycleID = itemCycleId;
    }

    if (itemCycleID) {
        return complianceItemService
            .getItemUserAssignments({ cplId, cycleId: itemCycleID, offset: 0, limit: LARGET_LIMIT, searchBy, statusId, a48statusId, approverUserId, groupIds })
            .toPromise();
    }
    else {
        return complianceItemService
            .getUserUserAssignments({ cplId, offset: 0, limit: LARGET_LIMIT, searchBy, statusId, a48statusId, approverUserId, groupIds })
            .toPromise();
    }
}

export const PrintStates: Ng2StateDeclaration[] = [
    {
        name: 'print.request',
        url: '/request',
        abstract: true
    },
    {
        name: 'print.roster',
        url: '/roster/:cplId/:itemCycleID?statusId&searchBy&act48Status&approverUserId&groupIds',
        views: {
            'content@print': {
                component: PrintRosterComponent
            }
        },
        resolve: [{
            token: 'cplItem',
            deps: [ComplianceItemService, Transition],
            resolveFn: resolveComplianceItem
        },
        {
            token: 'itemCycles',
            deps: [Transition, ComplianceItemService, 'cplItem'],
            resolveFn: resolveItemCycles
        },
        ]
    },
    {
        name: 'print.item',
        url: '/item/:cplId/:itemCycleID?statusId&searchBy&act48Status&approverUserId&groupIds',
        views: {
            'content@print': {
                component: PrintItemComponent
            }
        },
        params: {
            searchBy: {
                raw: true,
                value: '',
            },
            groupIds: {
                array: true
            }
        },
        resolve: [{
            token: 'cplItem',
            deps: [ComplianceItemService, Transition],
            resolveFn: resolveComplianceItem
        }, {
            token: 'itemCycles',
            deps: [Transition, ComplianceItemService, 'cplItem'],
            resolveFn: resolveItemCycles
        }]
    },
    {
        name: 'print.request.roster',
        url: '/roster/:requestId',
        abstract: true,
        resolve: [{
            token: 'cplItem',
            deps: [RequestItemService, Transition],
            resolveFn: resolveRequestItem
        }]
    }, , {
        name: 'print.request.roster.full',
        url: '/full',
        views: {
            'content@print': {
                component: PrintRosterComponent
            }
        },
        resolve: [{
            token: "users",
            deps: [Transition, UserRequestService],
            resolveFn: resolveUsers_Full
        }]
    }, {
        name: 'print.request.roster.filtered',
        url: '/filtered?statusId&userArchivedFilter&searchBy&act48Status&approverUserId&groupIds',
        params: {
            userArchivedFilter: {
                type: 'int',
                value: UserArchivedFilter.Active
            },
            groupIds: {
                array: true
            }
        },
        views: {
            'content@print': {
                component: PrintRosterComponent
            }
        },
        resolve: [{
            token: "users",
            deps: [Transition, UserRequestService, SessionService, RequestsResolver],
            resolveFn: resolveUsers_Filtered
        }]
    }
];